import { useEffect, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ClipboardList, X } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { HeroVorgangName } from "@/components/portal/HeroVorgangName"
import { darfAufgabeAnlegen } from "@/lib/aufgabeVerknuepfung"
import { herkunftHinweis, useAufgabeVerknuepfung } from "@/hooks/useAufgabeVerknuepfung"
import type { DraftDetail } from "./types"

type ContactItem = { id: number; first_name?: string | null; last_name?: string | null; company_name?: string | null; email?: string | null; phone_mobile?: string | null }
type ProjectItem = { id: number; name: string; code?: string | null; contact_name?: string | null; status?: string | null }
type MonteurItem = { id: number; hero_user_id: number | null; name: string }

/**
 * Modal: Aufgabe direkt aus einer E-Mail anlegen — unabhaengig vom Plan-Workflow.
 * Vorbefuellt mit Mail-Subject (Title), Sender + Body (Comment), Default-Monteur.
 * Kontakt + Projekt sind vorbelegt, sobald das Portal sie zur Mail kennt (#108) —
 * aenderbar und entfernbar. Ohne beides landet die Aufgabe in HERO + /tasks ohne Verknuepfung.
 * → POST /api/v1/emails/{id}/create-task → HERO-Task + lokale DB-Task.
 */
export function CreateTaskModal({
  open, onClose, detail, planTask,
}: {
  open: boolean
  onClose: () => void
  detail: DraftDetail
  /** Aufgabe aus dem Plan, falls es einen gibt — hat Vorrang vor jeder
   *  eigenen Vorbefuellung. Siehe useEffect unten. */
  planTask?: { title?: string; body?: string } | null
}) {
  const [title, setTitle] = useState("")
  const [body, setBody] = useState("")
  const [targetUserId, setTargetUserId] = useState<number | null>(null)
  const [contactId, setContactId] = useState<number | null>(null)
  const [contactLabel, setContactLabel] = useState<string>("")
  const [projectId, setProjectId] = useState<number | null>(null)
  const [projectLabel, setProjectLabel] = useState<string>("")
  const [submitting, setSubmitting] = useState(false)
  // #108: Herkunft der Vorbelegung („vom Plan erkannt“) — verschwindet, sobald
  // jemand selbst waehlt. `angefasst` verhindert, dass ein spaet eintreffender
  // Vorschlag eine bewusste Wahl (oder das bewusste Entfernen) ueberschreibt.
  const [contactHinweis, setContactHinweis] = useState("")
  const [projectHinweis, setProjectHinweis] = useState("")
  const angefasst = useRef({ contact: false, project: false })
  const vorschlag = useAufgabeVerknuepfung(detail.email_id, open)

  const monteure = useQuery({
    queryKey: ["monteure", detail.tenant_slug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/calendar/monteure?tenant=${encodeURIComponent(detail.tenant_slug)}`, { credentials: "include" })
      if (!r.ok) return { items: [] as MonteurItem[] }
      return r.json() as Promise<{ items: MonteurItem[] }>
    },
    staleTime: 1000 * 60 * 5,
    enabled: open && !!detail.tenant_slug,
  })

  const tenantConfig = useQuery({
    queryKey: ["tenant-detail", detail.tenant_slug],
    queryFn: async () => {
      const r = await fetch(`/api/v1/tenants/${encodeURIComponent(detail.tenant_slug)}`, { credentials: "include" })
      if (!r.ok) return null
      const t = await r.json()
      return { main_partner_id: t?.booking_config?.main_partner_id ?? null } as { main_partner_id: number | null }
    },
    staleTime: 1000 * 60 * 5,
    enabled: open && !!detail.tenant_slug,
  })

  // Modal-Open: Felder vorbefuellen + Projekt-Default aus hero_action
  useEffect(() => {
    if (!open) return
    const sender = detail.sender || detail.sender_email || ""
    const subject = detail.subject || "Anfrage klaeren"

    // Reihenfolge der Vorbefuellung, absteigend nach Qualitaet:
    //
    // 1. Der Plan. Er kommt vom Planer-Modell (Sonnet) und formuliert die
    //    Aufgabe aus — mit Namen, Belegnummern und dem, was konkret zu tun
    //    ist. Fuer genau diese Mail wurde dafuer bereits bezahlt; es waere
    //    unsinnig, stattdessen die knappere Analyse zu nehmen.
    // 2. Der Mailtext selbst.
    //
    // Bis 11.08.2026 begann die Kette erst bei 2. Wer im Review eine Aufgabe
    // von Hand anlegte, bekam deshalb einen anderen (kuerzeren) Text als der
    // Planer daneben vorschlug.
    //
    // 🔴 `detail.handlungsbedarf` stand hier bis zum 26.08.2026 als Stufe 2 und
    // ist RAUS (#46). Es ist die Begruendung der EINSTUFUNG, nicht eine
    // Aufgabe: der Analyse-Prompt schreibt fuer Spam/Phishing woertlich
    // `handlungsbedarf="Ignorieren/Löschen"` plus Begruendung
    // (`services/claude_email.py`). Beim Kunden landete dadurch
    // „Ignorieren/Loeschen - Verdaechtige E-Mail mit Phishing-Merkmalen.
    // Absender gibt sich als Versicherungsmakler aus…" — und weil die
    // Einstufung in dem Fall auch noch falsch war (#38), doppelt unbrauchbar.
    // Der Mailauszug ist als Rueckfall schlechter formuliert, aber wenigstens
    // das, worum es geht.
    const planTitel = (planTask?.title || "").trim()
    const planText = (planTask?.body || "").trim()
    const mailBody = (detail.original_body || detail.original_excerpt || "").trim()

    setTitle((planTitel || subject).slice(0, 200))
    if (planText) {
      setBody(planText)
    } else if (mailBody) {
      setBody(`Mail von ${sender}:\n\n${mailBody}`)
    } else {
      setBody(`Mail von ${sender} — siehe Original im Review.`)
    }
    // Projekt-Default aus hero_action wenn vorhanden
    const ha = detail.hero_action
    if (ha?.project_id) {
      setProjectId(ha.project_id)
      // #98: leer statt „Projekt #…" — die Auswahl schlaegt dann in HERO nach.
      setProjectLabel(ha.project_title || "")
    } else {
      setProjectId(null); setProjectLabel("")
    }
    setContactId(null); setContactLabel("")
    setContactHinweis(""); setProjectHinweis("")
    angefasst.current = { contact: false, project: false }
  }, [open, detail])

  // #108: Kontakt + Vorgang aus dem, was das Portal zur Mail kennt. Steht NACH
  // dem Effekt oben und haengt ebenfalls an `detail` — sonst leert ein
  // Nachladen der Mail die Vorbelegung wieder.
  useEffect(() => {
    if (!open || !vorschlag.data) return
    const { contact, project } = vorschlag.data
    if (contact && !angefasst.current.contact) {
      setContactId(contact.id); setContactLabel(contact.name)
      setContactHinweis(herkunftHinweis(contact))
    }
    if (project && !angefasst.current.project) {
      setProjectId(project.id); setProjectLabel(project.bezeichnung)
      setProjectHinweis(herkunftHinweis(project))
    }
  }, [open, detail, vorschlag.data])

  // Default-Monteur (main_partner_id → hero_user_id)
  useEffect(() => {
    if (!open) return
    if (targetUserId !== null) return
    const main = tenantConfig.data?.main_partner_id
    if (main) {
      const m = (monteure.data?.items || []).find((x) => x.id === main)
      if (m?.hero_user_id) { setTargetUserId(m.hero_user_id); return }
    }
    const first = (monteure.data?.items || []).find((m) => m.hero_user_id != null)
    if (first?.hero_user_id) setTargetUserId(first.hero_user_id)
  }, [open, monteure.data, tenantConfig.data, targetUserId])

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast("Titel fehlt", "error")
      return
    }
    // #149 (+ #22): Hinweis ohne Mitarbeiter / ohne Projekt — nur warnen.
    if (!(await darfAufgabeAnlegen({ mitarbeiter: targetUserId, contactId, projectId }))) return
    setSubmitting(true)
    try {
      const targetMonteur = (monteure.data?.items || []).find((m) => m.hero_user_id === targetUserId)
      const r = await fetch(`/api/v1/emails/${detail.email_id}/create-task`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          target_user_id: targetUserId,
          target_user_name: targetMonteur?.name || null,
          contact_id: contactId,
          project_match_id: projectId,
        }),
      })
      if (!r.ok) {
        const txt = await r.text().catch(() => "")
        throw new Error(`HTTP ${r.status}: ${txt.slice(0, 200)}`)
      }
      const j = await r.json() as { ok: boolean; hero_task_id: number | null; local_task_id: number }
      if (j.hero_task_id) toast(`✓ Aufgabe in HERO erstellt (#${j.hero_task_id})`)
      else toast("✓ Aufgabe lokal erstellt (HERO-Sync fehlgeschlagen)", "error")
      onClose()
    } catch (e) {
      toast("Fehler: " + (e instanceof Error ? e.message : String(e)), "error")
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null
  const items = monteure.data?.items || []

  return (
    <div
      className="pp-overlay"
      data-kontext-popup="Aufgabe anlegen"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: "#fff", borderRadius: 12, width: "100%", maxWidth: 560,
        maxHeight: "90vh", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,.25)",
      }}>
        <div style={{
          padding: "14px 20px", borderBottom: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <ClipboardList size={16} color="#3C3489" />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Aufgabe in HERO anlegen</h3>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label className="pp-form-label">Titel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Kurze Bezeichnung der Aufgabe"
              style={{ fontSize: 13 }}
              autoFocus
            />
          </div>

          <div>
            <label className="pp-form-label">Beschreibung / Kontext</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              style={{ fontSize: 12.5, fontFamily: "inherit", resize: "vertical" }}
            />
          </div>

          <ContactPicker
            tenant={detail.tenant_slug}
            selectedId={contactId}
            selectedLabel={contactLabel}
            hinweis={contactHinweis}
            onSelect={(id, label) => {
              angefasst.current.contact = true
              setContactId(id); setContactLabel(label); setContactHinweis("")
            }}
          />

          <ProjectPicker
            tenant={detail.tenant_slug}
            selectedId={projectId}
            selectedLabel={projectLabel}
            hinweis={projectHinweis}
            onSelect={(id, label) => {
              angefasst.current.project = true
              setProjectId(id); setProjectLabel(label); setProjectHinweis("")
            }}
          />

          <div>
            <label className="pp-form-label">Empfaenger (Monteur in HERO)</label>
            <select
              value={targetUserId ?? ""}
              onChange={(e) => setTargetUserId(e.target.value ? parseInt(e.target.value) : null)}
              style={{ fontSize: 13 }}
            >
              <option value="">— Monteur waehlen —</option>
              {items.filter((m) => m.hero_user_id != null).map((m) => (
                <option key={m.id} value={m.hero_user_id!}>{m.name}</option>
              ))}
            </select>
            <p style={{ fontSize: 12, color: "#888780", marginTop: 4 }}>
              Default = Standard-Monteur aus Tenant-Config.
            </p>
          </div>
        </div>

        <div style={{
          padding: "12px 20px", borderTop: "0.5px solid rgba(44,44,42,0.1)",
          display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button type="button" onClick={onClose} className="pp-btn pp-btn-sm" disabled={submitting}>
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !title.trim()}
            style={{
              background: "#3C3489", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: 6, fontSize: 13, fontWeight: 500,
              cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? "Erstelle…" : "✓ Aufgabe anlegen"}
          </button>
        </div>
      </div>
    </div>
  )
}


function ContactPicker({
  tenant, selectedId, selectedLabel, hinweis, onSelect,
}: {
  tenant: string
  selectedId: number | null
  selectedLabel: string
  hinweis?: string
  onSelect: (id: number | null, label: string) => void
}) {
  const [q, setQ] = useState("")
  const [results, setResults] = useState<ContactItem[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.length < 2) { setResults([]); return }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await fetch(`/api/v1/contacts?tenant=${encodeURIComponent(tenant)}&q=${encodeURIComponent(query)}&limit=10`, { credentials: "include" })
        if (!r.ok) { setResults([]); return }
        const d = await r.json()
        setResults(d.items || [])
      } catch { setResults([]) }
      finally { setSearching(false) }
    }, 250)
  }

  return (
    <div>
      <label className="pp-form-label">Kontakt-Verknüpfung (optional)</label>
      {selectedId ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--accent-color)" }}>
            {selectedLabel || "Kontakt"}
            {hinweis && <span data-testid="kontakt-herkunft" style={{ display: "block", fontSize: 11, fontWeight: 400, color: "var(--text-muted)" }}>{hinweis}</span>}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={() => { onSelect(null, ""); setQ(""); setResults([]) }}
            className="pp-btn pp-btn-sm" title="Verknüpfung entfernen">
            <X size={12} />
          </button>
        </div>
      ) : (
        <div>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); doSearch(e.target.value) }}
            placeholder="Kontakt suchen (Name, E-Mail, Telefon…)"
            style={{ fontSize: 13 }}
          />
          {searching && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 6 }}>Suche…</div>}
          {results.length > 0 && (
            <div style={{ marginTop: 4, maxHeight: 160, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 6 }}>
              {results.map((c) => {
                const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || `Kontakt #${c.id}`
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => { onSelect(c.id, name); setQ(""); setResults([]) }}
                    style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border-color)", background: "none", border: "none", cursor: "pointer" }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {c.email || c.phone_mobile || ""}{c.email || c.phone_mobile ? " · " : ""}#{c.id}
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}


function ProjectPicker({
  tenant, selectedId, selectedLabel, hinweis, onSelect,
}: {
  tenant: string
  selectedId: number | null
  selectedLabel: string
  hinweis?: string
  onSelect: (id: number | null, label: string) => void
}) {
  const [q, setQ] = useState("")
  const [results, setResults] = useState<ProjectItem[]>([])
  const [searching, setSearching] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const doSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (query.length < 2) { setResults([]); return }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const r = await fetch(`/api/v1/projects/search?tenant=${encodeURIComponent(tenant)}&q=${encodeURIComponent(query)}`, { credentials: "include" })
        if (!r.ok) { setResults([]); return }
        const d = await r.json()
        setResults(d.items || [])
      } catch { setResults([]) }
      finally { setSearching(false) }
    }, 250)
  }

  return (
    <div>
      <label className="pp-form-label">Projekt-Verknüpfung (optional)</label>
      {selectedId ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--accent-color)" }}>
            <HeroVorgangName tenantSlug={tenant} projectId={selectedId} bekannt={selectedLabel} />
            {hinweis && <span data-testid="projekt-herkunft" style={{ display: "block", fontSize: 11, fontWeight: 400, color: "var(--text-muted)" }}>{hinweis}</span>}
          </span>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={() => { onSelect(null, ""); setQ(""); setResults([]) }}
            className="pp-btn pp-btn-sm" title="Verknüpfung entfernen">
            <X size={12} />
          </button>
        </div>
      ) : (
        <div>
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); doSearch(e.target.value) }}
            placeholder="Projekt suchen (Name, Nummer, Kontakt…)"
            style={{ fontSize: 13 }}
          />
          {searching && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 6 }}>Suche…</div>}
          {results.length > 0 && (
            <div style={{ marginTop: 4, maxHeight: 160, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 6 }}>
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onSelect(p.id, p.name); setQ(""); setResults([]) }}
                  style={{ width: "100%", textAlign: "left", padding: "8px 10px", borderBottom: "1px solid var(--border-color)", background: "none", border: "none", cursor: "pointer" }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                    {p.contact_name ? `${p.contact_name} · ` : ""}
                    {p.code ? `${p.code} · ` : ""}{p.status || "kein Status"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
